import { Injectable } from '@angular/core';
import { SvgEditExtension, SvgEditExtensionObject } from '@models/svg-edit-extension';
import { CanvasService } from './canvas.service';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { SelectedElementsService } from './selected-elements.service';

@Injectable({ providedIn: 'root' })
export class ToggleExtensionService implements SvgEditExtension {
    name: string = 'buttonExtension';

    constructor(private plugin: ToggleExtensionPlugin) {
        this.init = this.init.bind(this);
    }

    init(): SvgEditExtensionObject {
        this.bindMouseEvents();
        return this.plugin;
    }

    addEventListeners(foreignObject) {
        this.plugin.addEventListeners(foreignObject);
    }

    private bindMouseEvents() {
        return this.plugin.bindMouseEvents();
    }
}

@Injectable({ providedIn: 'root' })
class ToggleExtensionPlugin implements SvgEditExtensionObject {
    constructor(
        private canvasService: CanvasService,
        private inputMode: CanvasInputModeService,
        private selectedElementsService: SelectedElementsService
    ) {}

    callback(deps: any) {}

    bindMouseEvents() {
        document.addEventListener('mousedown', this.handleMouseDown.bind(this));
        document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        document.addEventListener('mouseup', this.handleMouseUp.bind(this));
    }

    private handleMouseDown(event: MouseEvent) {}

    private handleMouseMove(event: MouseEvent) {}

    private handleMouseUp(event: MouseEvent) {
        if (this.inputMode.getMode() === CanvasInputMode.TOGGLE) {
            const svgRoot = this.canvasService.getSvgRoot();
            if (event.target !== svgRoot) {
                return;
            }
            const canvas = this.canvasService.getCanvas();

            const rootSctm = this.canvasService.getSCTM();
            const pt = this.canvasService.transformPoint(event.clientX, event.clientY, rootSctm);
            const mouseX = pt.x;
            const mouseY = pt.y;

            const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
            foreignObject.id = canvas.getNextId();
            foreignObject.setAttribute('x', mouseX.toString());
            foreignObject.setAttribute('y', mouseY.toString());
            foreignObject.setAttribute('type', CanvasInputMode.TOGGLE);

            foreignObject.setAttribute('width', '40px');
            foreignObject.setAttribute('height', '16px');
            foreignObject.setAttribute('pointer-events', 'all');

            const label = document.createElement('label');
            label.setAttribute('label', 'true');
            label.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            label.style.position = 'relative';
            label.style.display = 'contents';
            label.style.width = '100%';
            label.style.height = '100%';

            const input = document.createElement('input');
            input.setAttribute('input', 'true');
            input.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            input.type = 'checkbox';
            input.style.display = 'block';
            input.style.appearance = 'none';
            input.style.setProperty('-webkit-appearance', 'none');
            input.style.width = '100%';
            input.style.height = '100%';
            input.style.backgroundColor = '#2c3a48';
            input.style.borderRadius = '360px';

            const span = document.createElement('span');
            span.setAttribute('span', 'true');
            span.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            span.style.width = '30%';
            span.style.height = '80%';
            span.style.borderRadius = '50%';
            span.style.position = 'absolute';
            span.style.bottom = '10%';
            span.style.left = '7%';
            span.style.backgroundColor = '#FFFFFF';
            span.style.transition = '.4s';

            label.appendChild(input);
            label.appendChild(span);

            foreignObject.appendChild(label);

            const layer = svgRoot.querySelector('.layer');
            layer.appendChild(foreignObject);

            this.addEventListeners(foreignObject);
            canvas.addCommandToHistory(new canvas.history.InsertElementCommand(foreignObject));
            this.selectedElementsService.setSelectedElements([foreignObject]);
            this.inputMode.setMode(CanvasInputMode.SELECT);
        }
    }

    addEventListeners(foreignObject) {
        const span = foreignObject.querySelectorAll('[span="true"]')[0];
        const input = foreignObject.querySelectorAll('[input="true"]')[0];
        const label = foreignObject.querySelectorAll('[label="true"]')[0];

        foreignObject.addEventListener('updateToggleState', event => {
            if (event.detail.checked) {
                span.style.left = '60%';
                span.style.backgroundColor = '#9cb2fb';
                input.style.backgroundColor = '#485573';
                return;
            }
            span.style.backgroundColor = '#FFFFFF';
            span.style.left = '7%';
            input.style.backgroundColor = '#2c3a48';
        });

        const observer = new MutationObserver(() => {
            label.style.position = 'relative';
            label.style.display = 'contents';
            label.style.width = '100%';
            label.style.height = '100%';

            input.style.display = 'block';
            input.style.appearance = 'none';
            input.style.setProperty('-webkit-appearance', 'none');
            input.style.width = '100%';
            input.style.height = '100%';
            input.style.backgroundColor = '#2c3a48';
            input.style.borderRadius = '360px';

            span.style.width = '30%';
            span.style.height = '80%';
            span.style.borderRadius = '50%';
            span.style.position = 'absolute';
            span.style.bottom = '10%';
            span.style.left = '7%';
            span.style.backgroundColor = '#FFFFFF';
            span.style.transition = '.4s';
        });

        observer.observe(foreignObject, {
            attributes: true,
            attributeFilter: ['x', 'y', 'width', 'height'],
        });
    }
}
